import type { Command } from 'commander';
import { readFileSync } from 'node:fs';
import { asPaginatedBody, buildUserAgent, createApiClient, throwForHttpStatus } from '@wport/core';
import { CLI_SOURCE, resolveContext } from '../../lib/global-opts';
import { CliError, ExitCode } from '../../lib/errors';
import { dim, printJson, printTable, printWarn } from '../../lib/output';
import { parseFieldsList, pickPaths } from '../../lib/path-utils';
import type { operations } from '@wport/core';

interface SearchFlags {
	keyword?: string;
	location?: string[];
	category?: string[];
	page?: number;
	pageSize?: number;
	jsonQuery?: string;
	fields?: string;
	minimal?: boolean;
}

/**
 * Compact field set for `--minimal` — the columns an agent almost always wants from a
 * search result, mirroring the table view minus the noise. Keeps `enc_id` first so the
 * output is directly pipeable into `jobs view -`.
 */
const MINIMAL_SEARCH_FIELDS = ['enc_id', 'title', 'company_name', 'area_display', 'salary_display'];

/**
 * Query type derived from the generated OpenAPI schema for GET /api/jobs/search.
 * Adding a new field on the backend DTO + re-running gen:openapi will make this type
 * widen automatically; any flag we forget to map will surface as a TS error inside
 * buildQuery() instead of being silently dropped at runtime.
 */
type SearchQuery = NonNullable<operations['JobsController_searchJobs']['parameters']['query']>;

interface JobSearchItem {
	enc_id?: string;
	enc_company_id?: string;
	company_name?: string;
	company_logo_url?: string;
	title?: string;
	area_display?: string;
	salary_display?: string;
	salary_currency_code?: string | null;
	tags?: string[];
	updated_at?: string;
	[k: string]: unknown;
}

export function registerJobsSearch(parent: Command): void {
	parent
		.command('search')
		.description(
			'Search public job listings. Sort is server-controlled (publish date, or relevance when --keyword is set); ' +
				'the orderBy / order query params are silently ignored, so this CLI intentionally exposes no sort flags. ' +
				'Run `wport doctor` for the full list of server quirks.'
		)
		.option('-k, --keyword <text>', 'keyword search (title / company name etc.)')
		.option('-l, --location <code...>', 'area code (repeatable, e.g. 6001001000)')
		.option('-c, --category <code...>', 'job classification code (repeatable)')
		.option('-p, --page <n>', 'page number (default 1)', (v) => Number(v))
		.option('-s, --page-size <n>', 'page size (default 10, max 100)', (v) => Number(v))
		.option('--json-query <file>', 'read full query body from JSON file (overrides other flags)')
		.option(
			'--fields <list>',
			'keep only these fields in each JSON result (comma-separated dotted paths, e.g. enc_id,title). JSON output only.'
		)
		.option('--minimal', `shorthand for --fields ${MINIMAL_SEARCH_FIELDS.join(',')}. JSON output only.`)
		.action(async (flags: SearchFlags, command: Command) => {
			const ctx = resolveContext(command);
			const fields = resolveSearchFields(flags);
			const query = buildQuery(flags);

			const client = createApiClient({
				baseUrl: ctx.baseUrl,
				locale: ctx.locale,
				timeoutMs: ctx.timeoutMs,
				userAgent: buildUserAgent('wport-cli', __CLI_VERSION__),
				source: CLI_SOURCE,
			});

			// openapi-fetch consumes the response body itself; use `data` (success) or `error` (non-2xx).
			const { data, error, response } = await client.GET('/api/jobs/search', {
				params: { query },
			});
			if (!response.ok) throwForHttpStatus(response.status, error);

			const paged = asPaginatedBody<JobSearchItem>(data);

			if (ctx.format === 'json') {
				// Field projection is client-side: it trims tokens the model has to read, not
				// bytes-on-the-wire (the server has no projection param). Pagination metadata is
				// preserved by spreading `paged` and only replacing `data`.
				const body = fields ? { ...paged, data: paged.data.map((item) => pickPaths(item, fields)) } : paged;
				printJson(body);
				return;
			}

			// Table columns are fixed; --fields / --minimal are a JSON-only affordance. Warn
			// rather than silently ignore so the flag doesn't look broken.
			if (fields) {
				printWarn('--fields / --minimal only affect JSON output; ignored for table. Use --output json.', ctx.color);
			}

			printTable(
				paged.data,
				[
					{ header: 'ENC_ID', value: (r) => truncate(r.enc_id ?? '', 14) },
					{ header: 'TITLE', value: (r) => r.title ?? '', maxWidth: 36 },
					{ header: 'COMPANY', value: (r) => r.company_name ?? '', maxWidth: 20 },
					{ header: 'LOCATION', value: (r) => r.area_display ?? '', maxWidth: 18 },
					{ header: 'SALARY', value: (r) => r.salary_display ?? '', maxWidth: 18 },
					{ header: 'UPDATED', value: (r) => formatDate(r.updated_at), maxWidth: 12 },
				],
				ctx.color
			);

			const head = `Showing page ${paged.currentPage}/${paged.totalPages} (${paged.data.length} of ${paged.totalCount} results).`;
			const hint =
				paged.totalPages > paged.currentPage ? `  Next: wport jobs search --page ${paged.currentPage + 1}` : '';
			process.stdout.write(dim(head + hint, ctx.color) + '\n');
		});
}

function resolveSearchFields(flags: SearchFlags): string[] | undefined {
	if (flags.fields && flags.minimal) {
		throw new CliError('Use either --fields or --minimal, not both', ExitCode.InvalidArgument);
	}
	if (flags.minimal) return [...MINIMAL_SEARCH_FIELDS];
	if (flags.fields) return parseFieldsList(flags.fields);
	return undefined;
}

function buildQuery(flags: SearchFlags): SearchQuery {
	if (flags.jsonQuery) {
		// User-supplied JSON escape hatch — we trust the caller. Runtime validation will come
		// from the server side; this is the one spot the typed query is deliberately relaxed.
		return readJsonQuery(flags.jsonQuery) as SearchQuery;
	}
	const q: SearchQuery = {};
	if (flags.keyword) q.keyword = flags.keyword;
	if (flags.location?.length) q.area_codes = flags.location;
	if (flags.category?.length) q.job_classification_codes = flags.category;
	if (flags.page !== undefined) q.currentPage = flags.page;
	if (flags.pageSize !== undefined) q.pageSize = flags.pageSize;
	return q;
}

function readJsonQuery(path: string): Record<string, unknown> {
	let raw: string;
	try {
		raw = readFileSync(path, 'utf8');
	} catch (err) {
		throw new CliError(`Cannot read --json-query file ${path}: ${(err as Error).message}`, ExitCode.InvalidArgument);
	}
	try {
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
			throw new Error(
				Array.isArray(parsed)
					? 'JSON root is an array; expected an object with query fields'
					: 'JSON root must be an object'
			);
		}
		return parsed as Record<string, unknown>;
	} catch (err) {
		throw new CliError(`Invalid JSON in ${path}: ${(err as Error).message}`, ExitCode.InvalidArgument);
	}
}

function truncate(s: string, max: number): string {
	// Fast path: UTF-16 code units (string.length) are always >= codepoint count,
	// so if byte-length already fits we don't need codepoint counting.
	if (s.length <= max) return s;
	// Array.from iterates by code-point, so surrogate pairs (emoji, supplementary
	// plane chars) aren't split mid-character. Doesn't handle grapheme clusters
	// (combining marks), but covers the common bilingual / emoji case.
	const chars = Array.from(s);
	if (chars.length <= max) return s;
	return chars.slice(0, max - 1).join('') + '…';
}

function formatDate(s: string | undefined): string {
	if (!s) return '';
	const m = /^(\d{4}-\d{2}-\d{2})/.exec(s);
	return m ? m[1] : s;
}
